import type { VariantProps } from "tailwind-variants"
import { classNames, tv } from "../../utils"
import { Block, type BlockProps } from "../Block"
import { FlexColumn } from "../FlexColumn"
import { SpaceBetween } from "../SpaceBetween"

const skeletonVariants = tv({
  base: "size-full animate-pulse",
  variants: {
    variant: {
      default: "bg-bg-additional-2",
      "on-background": "bg-bg-additional-3",
    },
  },
})

export type SkeletonProps = {
  variant?: VariantProps<typeof skeletonVariants>["variant"]
} & BlockProps

/**
 * Use to show a placeholder while content is loading.
 */
export function Skeleton({ className, ...rest }: SkeletonProps) {
  return (
    <FlexColumn
      className={classNames(
        "size-full shrink-0 grow rounded-inherit",
        "*:not-first:mt-2",
        className,
      )}
      {...rest}
    />
  )
}

/**
 * Use to display row skeleton - usually for tabular data.
 */
export const SkeletonRow = SpaceBetween

/**
 * Use to display block skeleton - rectangular loading placeholder.
 */
export function SkeletonBlock({
  className,
  variant = "default",
  ...rest
}: SkeletonProps) {
  return (
    <Block
      className={classNames(
        skeletonVariants({ variant }),
        "rounded-inherit",
        className,
      )}
      {...rest}
    />
  )
}

/**
 * Use to display circular skeleton - for avatar placeholders.
 */
export function SkeletonCircle({
  className,
  variant = "default",
  ...rest
}: SkeletonProps) {
  return (
    <Block
      className={classNames(
        skeletonVariants({ variant }),
        "rounded-full",
        className,
      )}
      {...rest}
    />
  )
}

/**
 * Use to display line skeleton - for text placeholders.
 */
export function SkeletonLine({
  className,
  variant = "default",
  ...rest
}: SkeletonProps) {
  return (
    <Block
      className={classNames(
        skeletonVariants({ variant }),
        "h-[18px] rounded",
        className,
      )}
      {...rest}
    />
  )
}
